import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { internalChatEndpoint } from "@/config/endpoints";
import { useSocketListener } from "@/sockets/hooks/useSocketListener";
import { SocketEvents } from "@/sockets/socketEvents";
import { watchLeadChat } from "@/features/chat/utils/chatLiveRoom";

export interface ChatMessage {
  id: string;
  channel: "lead" | "child";
  leadId: number;
  childId: number | null;
  senderId: number;
  sender: { id: number; name: string };
  /** "Switch user": the REAL sender when a manager typed this while viewing
   *  as `sender`. Null on every ordinary message. Display only — `senderId`
   *  still decides own-message alignment, read receipts and edit/delete. */
  switchedByUser?: { id: number; name: string } | null;
  /** Written by an automation: the server already names the sender "System".
   *  Never shown as the reader's own message, even to the rule's maker who
   *  owns the row. */
  isSystem?: boolean;
  type: string;
  body: string | null;
  mediaUrl: string | null;
  mediaMime: string | null;
  mediaName: string | null;
  mediaSize: number | null;
  mirrorOfId: string | null;
  mentions: { userId: number; name: string }[] | null;
  replyTo: {
    id: string;
    body: string | null;
    type: string;
    sender: { id: number; name: string };
    switchedByUser?: { id: number; name: string } | null;
  } | null;
  readBy: { userId: number; readAt: string }[];
  createdAt: string;
  /** Set once the sender edits the message — renders an "edited" tag. */
  editedAt?: string | null;
}

interface ChatResponse {
  messages: ChatMessage[];
  nextCursor: string | null;
  hasMore: boolean;
}

function queryKey(leadId: number | string, childId?: number | string | null) {
  return childId
    ? ["internal-chat", String(leadId), "child", String(childId)]
    : ["internal-chat", String(leadId)];
}

/**
 * Merge a freshly fetched first page into what is already loaded. Server
 * rows win for the same id (tombstones / edits), older pages that were paged
 * in survive (the old code REPLACED the list with page 1 on every refetch,
 * so "Load older" was undone by your own next send).
 */
function mergeMessages(prev: ChatMessage[], fresh: ChatMessage[]) {
  const byId = new Map<string, ChatMessage>();
  for (const m of prev) byId.set(m.id, m);
  for (const m of fresh) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => {
    const t = Date.parse(a.createdAt) - Date.parse(b.createdAt);
    return t !== 0 ? t : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

export function useInternalChat(
  leadId: number | string | null,
  childId?: number | string | null
) {
  const qc = useQueryClient();
  const [allMessages, setAllMessages] = useState<ChatMessage[]>([]);
  const cursorRef = useRef<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  // Ref mirrors so an async "load older until found" loop reads live state
  // instead of the closure it was created with (synced after each commit;
  // the loop also writes them directly as it prepends pages).
  const messagesRef = useRef<ChatMessage[]>([]);
  const hasMoreRef = useRef(false);
  useEffect(() => {
    messagesRef.current = allMessages;
    hasMoreRef.current = hasMore;
  }, [allMessages, hasMore]);

  const url = leadId
    ? childId
      ? `${internalChatEndpoint}/${leadId}/child/${childId}`
      : `${internalChatEndpoint}/${leadId}`
    : null;

  const key = leadId
    ? queryKey(Number(leadId), childId)
    : ["internal-chat", "none"];

  const { data, isLoading, error } = useQuery<ChatResponse>({
    queryKey: key,
    queryFn: async () => {
      if (!url) return { messages: [], nextCursor: null, hasMore: false };
      const res = await axiosInstance.get(url);
      return res.data;
    },
    enabled: Boolean(leadId),
    staleTime: 1000 * 60 * 2,
  });

  // First page for THIS conversation → replace; a refetch of the SAME
  // conversation (after send / edit / delete, focus) → merge so pages that
  // were loaded via "Load older" are not thrown away. The cursor only moves
  // while nothing older than page 1 is loaded.
  const keyStr = key.join("/");
  const loadedKeyRef = useRef<string | null>(null);
  const pagedOlderRef = useRef(false);
  useEffect(() => {
    if (!data) return;
    if (loadedKeyRef.current !== keyStr) {
      loadedKeyRef.current = keyStr;
      pagedOlderRef.current = false;
      setAllMessages(data.messages);
      cursorRef.current = data.nextCursor;
      setHasMore(data.hasMore);
      return;
    }
    setAllMessages((prev) => mergeMessages(prev, data.messages));
    if (!pagedOlderRef.current) {
      cursorRef.current = data.nextCursor;
      setHasMore(data.hasMore);
    }
  }, [data, keyStr]);

  // LIVE ROOM: while this chat is mounted, sit in the lead's chat room so
  // new / edited / deleted / read pushes reach us even when we are not a
  // member of the lead (admins, managers). Replayed on reconnect.
  useEffect(() => {
    if (!leadId) return;
    return watchLeadChat(Number(leadId));
  }, [leadId]);

  // Socket: append new message in real-time
  useSocketListener(SocketEvents.INTERNAL_CHAT_NEW, (payload: any) => {
    if (!leadId) return;
    const msg = payload?.message as ChatMessage | undefined;
    if (!msg) return;

    const matchesLead =
      msg.leadId === Number(leadId) && !childId && msg.channel === "lead";
    const matchesChild =
      msg.leadId === Number(leadId) &&
      childId &&
      msg.childId === Number(childId) &&
      msg.channel === "child";

    if (matchesLead || matchesChild) {
      setAllMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
    }
  });

  // Socket: tombstone a deleted-for-everyone message in place. Matching by
  // message id is exact, so no lead/child channel checks are needed.
  useSocketListener(SocketEvents.INTERNAL_CHAT_DELETED, (payload: any) => {
    const messageId = payload?.messageId as string | undefined;
    if (!messageId) return;
    setAllMessages((prev) =>
      prev.map((m) =>
        m.id === messageId
          ? {
              ...m,
              type: "deleted",
              body: null,
              mediaUrl: null,
              mediaMime: null,
              mediaName: null,
              mediaSize: null,
            }
          : m
      )
    );
  });

  // Socket: sender edited a text message — swap body/mentions in place.
  useSocketListener(SocketEvents.INTERNAL_CHAT_EDITED, (payload: any) => {
    const messageId = payload?.messageId as string | undefined;
    if (!messageId || typeof payload?.body !== "string") return;
    setAllMessages((prev) =>
      prev.map((m) =>
        m.id === messageId
          ? {
              ...m,
              body: payload.body,
              mentions: Array.isArray(payload.mentions)
                ? payload.mentions
                : null,
              editedAt: payload.editedAt ?? new Date().toISOString(),
            }
          : m
      )
    );
  });

  // Socket: update read receipts
  useSocketListener(SocketEvents.INTERNAL_CHAT_READ, (payload: any) => {
    if (!leadId) return;
    const { messageIds, userId } = payload || {};
    if (!messageIds || !userId) return;

    setAllMessages((prev) =>
      prev.map((m) => {
        if (!messageIds.includes(m.id)) return m;
        if (m.readBy.some((r) => r.userId === userId)) return m;
        return {
          ...m,
          readBy: [...m.readBy, { userId, readAt: new Date().toISOString() }],
        };
      })
    );
  });

  // Load older messages
  const fetchOlder = useCallback(async () => {
    if (!url || !cursorRef.current || !hasMore) return;
    const res = await axiosInstance.get(url, {
      params: { cursor: cursorRef.current },
    });
    const older: ChatResponse = res.data;
    pagedOlderRef.current = true;
    cursorRef.current = older.nextCursor;
    setHasMore(older.hasMore);
    setAllMessages((prev) => mergeMessages(prev, older.messages));
  }, [url, hasMore]);

  /**
   * Make sure `messageId` is in `messages`, paging OLDER pages in until it
   * turns up — the reply-preview / mention "jump to message" needs the
   * original bubble in the DOM, and it may be many pages back. Bounded so a
   * message that was deleted before the pages existed can't spin forever.
   * Resolves true when the message is loaded.
   */
  const ensureMessageLoaded = useCallback(
    async (messageId: string, maxPages = 40): Promise<boolean> => {
      if (!url) return false;
      for (let i = 0; i <= maxPages; i++) {
        if (messagesRef.current.some((m) => m.id === messageId)) return true;
        if (!hasMoreRef.current || !cursorRef.current) return false;
        const res = await axiosInstance.get(url, {
          params: { cursor: cursorRef.current },
        });
        const older: ChatResponse = res.data;
        pagedOlderRef.current = true;
        cursorRef.current = older.nextCursor;
        hasMoreRef.current = older.hasMore;
        setHasMore(older.hasMore);
        const merged = mergeMessages(messagesRef.current, older.messages);
        messagesRef.current = merged;
        setAllMessages(merged);
        if (older.messages.length === 0) return false;
      }
      return messagesRef.current.some((m) => m.id === messageId);
    },
    [url],
  );

  return {
    messages: allMessages,
    isLoading,
    error,
    hasMore,
    fetchOlder,
    ensureMessageLoaded,
    refetch: () => qc.invalidateQueries({ queryKey: key }),
  };
}

/** Invalidate internal chat queries externally. */
export function refetchInternalChat() {
  // This is called from global socket events if needed
}
